import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref, watch } from 'vue';
import { useRouter } from 'vue-router/composables';
import type { ContentCollection } from '@ilteducation/content-collections-types';
import { defaultSubscriptions } from '@/foundations/helpers/defaultSubscriptions';
import { defaultRealms } from '@/foundations/helpers/defaultRealms';
import { getList, destroy } from '@/data-provider/contentCollections';
import ConfirmationDialog from '@/components/ConfirmationDialog.vue';
import eventBus from '@/foundations/services/eventBus';


export default /*#__PURE__*/_defineComponent({
  __name: 'CollectionTable',
  setup(__props) {

const headers = [
  { text: 'Id', align: 'start', sortable: false, value: 'id' },
  { text: 'Name', value: 'name' },
  { text: 'Market', value: 'realm', sortable: false },
  { text: 'Context', value: 'context' },
  { text: 'State', value: 'state', sortable: false },
  { text: 'Actions', value: 'actions', sortable: false },
];

const router = useRouter();

const confirmDialog = ref<InstanceType<typeof ConfirmationDialog> | null>(null);
const isLoading = ref<boolean>(false);
const selected = ref<string[]>([]);
const items = ref<ContentCollection[]>([]);
const cursor = ref<string | null | undefined>(undefined);
const search = ref<string | undefined>();
const realm = ref<string | undefined>('se');
const context = ref<string | undefined>();

const usSuffix = computed(() => (realm.value === 'us' ? '-us' : ''));
const defaultMarketName = computed(() => defaultRealms.find((r) => r.code === realm.value)?.realm || realm.value);

const loadItems = async () => {
  try {
    isLoading.value = true;
    const filter = { realm: realm.value, context: context.value };
    const { data, cursor: c } = await getList(filter, cursor.value);
    items.value = [...items.value, ...data];
    cursor.value = c ?? null;
  } catch (error) {
    cursor.value = null;
    console.error(error);
    eventBus.$emit('snackbarV2.open', { text: `Failed to load collections!`, color: 'error' });
  } finally {
    isLoading.value = false;
  }
};

const fetchItems = async () => {
  do {
    // This is bad, but the alternative is not better to track empty states. And do recursive calls.
    // eslint-disable-next-line no-await-in-loop
    await loadItems();
  } while (cursor.value && cursor.value.length > 0);
};

watch(
  [realm, context],
  async () => {
    items.value = [];
    cursor.value = undefined;
    if (realm.value && context.value) {
      await fetchItems();
    } else {
      await loadItems();
    }
  },
  { deep: true },
);

const onNext = async () => {
  await loadItems();
};

const onContinues = async () => {
  if (confirmDialog.value !== null &&
    (await confirmDialog.value.open(
      `Load all collections for ${defaultMarketName.value}?`,
      `You are about to load all collections for ${defaultMarketName.value}. This operation may take a while!`,
      'No',
      'Yes',
    ))
  ) {
    await fetchItems();
  }
};

const onDeleteItem = async (item: ContentCollection) => {
  if (confirmDialog.value !== null &&
    (await confirmDialog.value.open(
      `Delete Collection #${item.id} ?`,
      `You are about to delete collection ${item.id} . This operation is irreversible!`,
      'No',
      'Yes',
    ))
  ) {
    try {
      isLoading.value = true;
      await destroy(item.realm, item.id);
      items.value = items.value.filter((i) => i.id !== item.id);
      eventBus.$emit('snackbarV2.open', { text: `Collection removed successfully!`, color: 'success' });
    } catch (error) {
      eventBus.$emit('snackbarV2.open', { text: `Failed to remove collection!`, color: 'error' });
    } finally {
      isLoading.value = false;
    }
  }
};

const onDeleteSelected = async () => {
  if (confirmDialog.value !== null &&
    (await confirmDialog.value.open(
      'Not Implemented: Delete Selected Collections?',
      `You are about to delete ${selected.value.length} collections. This operation is irreversible!`,
      'No',
      'Yes',
    ))
  ) {
    // TODO: delete selected items
    console.debug('Delete', selected.value);
  }
};

const getRouteObject = (identifier?: string, action: string) => {
  let routeObject = {
    name: `collections${usSuffix.value}_${action}`,
    params: { id: identifier },
    query: { realm: realm.value },
  };
  if (action === 'clone' || !identifier) {
    routeObject = {
      name: `collections${usSuffix.value}_create`,
      query: { source: identifier, realm: realm.value },
    };
  }
  return routeObject;
};

onMounted(async () => {
  await loadItems();
});

return { __sfc: true,headers, router, confirmDialog, isLoading, selected, items, cursor, search, realm, context, usSuffix, defaultMarketName, loadItems, fetchItems, onNext, onContinues, onDeleteItem, onDeleteSelected, getRouteObject, defaultSubscriptions, defaultRealms, ConfirmationDialog }
}

})